<template>
    <div v-if="loading" class="modal-form-loading">{{ $t("Loading") }}...</div>
    <div
        v-if="sessions.length > 1"
        :disabled="loading || sessions.length === 0 || (!sAllSessions && sSessions.length === 0)"
        class="d-flex justify-content-end align-items-end"
    >
        <button class="btn btn-cancel mb-3" @click="closeSelectedSessions">
            <IconItem icon="mdi:close" /><span class="text-responsive">{{ $t("Close selected sessions") }}</span>
        </button>
    </div>

    <div v-if="!loading" class="table-responsive">
        <table class="table">
            <thead>
                <tr>
                    <th v-if="sessions.length > 1" scope="col">
                        <div class="form-check">
                            <input
                                v-model="sAllSessions"
                                @change="onChangeSelectAllSessions()"
                                class="form-check-input cursor-pointer"
                                type="checkbox"
                                role="switch"
                            />
                        </div>
                    </th>
                    <th scope="col">{{ $t("Date") }}</th>
                    <th scope="col">{{ $t("IP address") }}</th>
                    <th scope="col">{{ $t("Browser") }}</th>
                    <th scope="col">{{ $t("Operating system") }}</th>
                    <th scope="col"></th>
                </tr>
            </thead>
            <tbody v-for="s in sessions" :key="s.session">
                <tr>
                    <td v-if="sessions.length > 1">
                        <div v-if="!s.current" class="form-check">
                            <input
                                v-model="s.selected"
                                @change="onChangeSelectSession(s)"
                                class="form-check-input cursor-pointer"
                                type="checkbox"
                                role="switch"
                            />
                        </div>
                    </td>
                    <td scope="row">{{ renderDate(s.created) }}</td>
                    <td>
                        <a :href="getIPLink(s.remote)" warning="no" target="_blank" rel="noopener noreferrer" class="external-link">{{
                            s.remote
                        }}</a>
                    </td>
                    <td>{{ s.browser }}</td>
                    <td>{{ s.os }}</td>
                    <td>
                        <template v-if="s.current">({{ $t("Current session") }})</template>
                        <button v-if="!s.current" @click="closeSession(s)" type="button" class="btn btn-primary">
                            <IconItem icon="mdi:logout" /><span class="text-responsive">{{ $t("Close") }}</span>
                        </button>
                    </td>
                </tr>
            </tbody>
        </table>
    </div>
</template>

<script lang="ts">
import { useVModel } from "@/utils/v-model";
import { defineComponent } from "vue";
import { getUniqueStringId } from "@/utils/unique-id";
import { Timeouts } from "@/utils/timeout";
import { Request } from "@asanrom/request-browser";
import { ApiAccount } from "@/api/api-group-account";
import { renderDateAndTime } from "@/utils/time-utils";

export default defineComponent({
    name: "SessionsPage",
    setup(props) {
        return {
            displayStatus: useVModel(props, "display"),
            loadRequestId: getUniqueStringId(),
        };
    },
    data: function () {
        return {
            loading: false,
            notFound: false,
            error: "",
            sessions: [],

            sSessions: [],
            sAllSessions: false,
        };
    },
    methods: {
        onChangeSelectAllSessions: function () {
            if (this.sAllSessions) {
                for (const s of this.sessions) {
                    if (!s.current) {
                        s.selected = true;
                        if (!this.sSessions.includes(s.session)) {
                            this.sSessions.push(s.session);
                        }
                    }
                }
            } else {
                for (const s of this.sessions) {
                    if (!s.current) {
                        s.selected = false;
                        if (this.sSessions.includes(s.session)) {
                            this.sSessions = this.sSessions.filter((item) => item !== s.session);
                        }
                    }
                }
            }
        },

        onChangeSelectSession: function (s) {
            if (s.current) {
                s.selected = false;
                return;
            }

            if (s.selected) {
                if (!this.sSessions.includes(s.session)) {
                    this.sSessions.push(s.session);
                }
            } else {
                if (this.sSessions.includes(s.session)) {
                    this.sSessions = this.sSessions.filter((item) => item !== s.session);
                }
            }

            this.sAllSessions = this.sSessions.length === this.sessions.length - 1;
        },

        renderDate: function (d: number): string {
            return renderDateAndTime(d, this.$t);
        },

        load: function () {
            Timeouts.Abort(this.loadRequestId);
            Request.Abort(this.loadRequestId);

            this.loading = true;

            Request.Pending(this.loadRequestId, ApiAccount.GetSessions())
                .onSuccess((response) => {
                    this.loading = false;
                    this.notFound = false;
                    for (const s of response) {
                        this.sessions.push({
                            browser: s.browser,
                            created: s.created,
                            current: s.current,
                            os: s.os,
                            remote: s.remote,
                            session: s.session,
                            selected: false,
                        });
                    }
                })
                .onRequestError((err, handleErr) => {
                    handleErr(err, {
                        unauthorized: () => {
                            this.$requireLogin();
                        },
                        temporalError: () => {
                            Timeouts.Set(this.loadRequestId, 1500, this.load.bind(this));
                        },
                    });
                })
                .onUnexpectedError((err) => {
                    console.error(err);
                    // Retry
                    Timeouts.Set(this.loadRequestId, 1500, this.load.bind(this));
                });
        },

        closeSession: function (s) {
            this.$askUserConfirmation({
                title: this.$t("Close session"),
                message: this.$t("Are you sure you want to close this session?"),
                icon: "mdi:alert-outline",
                callback: () => {
                    Request.Do(ApiAccount.CloseSession(s.session)).onSuccess(() => {
                        for (let i = 0; i < this.sessions.length; i++) {
                            if (this.sessions[i].session === s.session) {
                                this.sessions.splice(i, 1);
                                break;
                            }
                        }
                        this.$showSnackBar("Session closed successfully!");
                    });
                },
            });
        },

        closeSelectedSessions: function () {
            this.$askUserConfirmation({
                title: this.$t("Close sessions"),
                message: this.$t("Are you sure you want to close the selected sessions?"),
                icon: "mdi:alert-outline",
                callback: () => {
                    this.doCloseSelectedSessions();
                },
            });
        },

        doCloseSelectedSessions: async function () {
            for (const s of this.sSessions) {
                const response = await this.closeSessionPromise(s);

                if (response) {
                    for (let i = 0; i < this.sessions.length; i++) {
                        if (this.sessions[i].session === s) {
                            this.sessions.splice(i, 1);
                            break;
                        }
                    }
                }
            }

            this.sAllSessions = false;
            this.sSessions = [];
            this.$showSnackBar("Sessions closed successfully!");
        },

        closeSessionPromise: function (session: string) {
            return new Promise<boolean>((resolve, reject) => {
                Request.Do(ApiAccount.CloseSession(session))
                    .onSuccess(() => {
                        resolve(true);
                    })
                    .onRequestError(() => {
                        reject(false);
                    })
                    .onUnexpectedError(() => {
                        reject(false);
                    });
            });
        },

        getIPLink: function (ip: string) {
            return "https://ip-api.com/#" + encodeURIComponent(ip);
        },
    },
    mounted: function () {
        this.load();
    },
    beforeUnmount: function () {
        Timeouts.Abort(this.loadRequestId);
        Request.Abort(this.loadRequestId);
    },
});
</script>

<style scoped>
@import "@/style/routes/configuration/user-config.css";
</style>
